import {
  IconAutomation,
  IconLogout,
  IconPencil,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import clsx from "clsx";
import OrganisationAccountSettings from "features/Organisation/features/OrganisationAccountSettings";
import OrganisationUserManagement from "features/Organisation/features/OrganisationUserManagement";
import useToast from "hooks/useToast";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { globalStore } from "store/GlobalStore";
import { organisationStore } from "features/Organisation/OrganisationStore";
import { USER_ROLES, isTabletSize } from "utils";
import { sidebarMenuStore } from "../SidebarMenuStore";
import InviteMember from "features/Organisation/features/InviteMember";
import Button from "components/Button";
import { ROUTES_ENUM } from "routes/consts";

function OrganisationSettings() {
  const navigate = useNavigate();
  const toast = useToast();
  const { currentOrganisation } = sidebarMenuStore;
  const { t } = useTranslation();
  const [openMembers, setOpenMembers] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const [leaveDisabled, setLeaveDisabled] = useState<boolean>(false);
  const isUserAdmin = currentOrganisation?.role === USER_ROLES[0].id;
  const [openInvite, setOpenInvite] = useState(false);
  const showInvite = () => setOpenInvite((state) => !state);

  async function checkOrganisationLeaveEnabled() {
    if (isUserAdmin) {
      await organisationStore
        .hasOrganisationMultipleAdmins(currentOrganisation)
        .then((multipleAdmins) =>
          setLeaveDisabled(!multipleAdmins && isUserAdmin)
        );
    } else {
      setLeaveDisabled(false);
    }
  }

  useEffect(() => {
    checkOrganisationLeaveEnabled();
  }, [currentOrganisation]);

  const onOpenMembers = () => {
    setOpenMembers(true);
    if (isTabletSize()) {
      globalStore.setSidebarOpened(false);
    }
  };

  const onOpenSettings = () => {
    setOpenSettings(true);
    if (isTabletSize()) {
      globalStore.setSidebarOpened(false);
    }
  };

  const organisationSettingsItems = [
    {
      icon: <IconUsers width={20} className="mr-3 !min-w-4" stroke={2.5} />,
      label: t("organisationSettings.manageMembers"),
      onClick: () => onOpenMembers(),
      hidden: !isUserAdmin,
      disabled: false,
    },
    {
      icon: <IconPencil width={20} className="mr-3 !min-w-4" stroke={2.5} />,
      label: t("organisationSettings.editOrganisation"),
      onClick: () => onOpenSettings(),
      hidden: !isUserAdmin,
      disabled: false,
    },
    {
      icon: <IconLogout width={20} className="mr-3 !min-w-4" stroke={2.5} />,
      label: t("organisationSettings.leaveOrganisation"),
      onClick: () => leaveOrganisation(),
      className: "text-red-700",
      disabled: leaveDisabled,
      hidden: false,
    },
  ];

  async function leaveOrganisation() {
    if (!currentOrganisation?.id) return;
    organisationStore
      .leaveOrganisation(currentOrganisation)
      .then(async (response) => {
        if (!response) return;
        if (response.success) {
          await organisationStore.getAllOrganisations().then(() => {
            sidebarMenuStore.setCurrentOrganisation(
              organisationStore.organisations.items[0]
            );
            navigate(`/${ROUTES_ENUM.DASHBOARD}`, { replace: true });
            toast(
              t("organisationSettings.myOrganisation.leftOrganisationSuccess"),
              "success"
            );
          });
        } else {
          toast(response.message.toString(), "warning");
        }
      })
      .catch((err) => toast(t("commons.genericErrorMessage", err), "warning"));
  }

  const openRulesEditor = () => {
    navigate(`/${currentOrganisation?.id}/${ROUTES_ENUM.RULES_EDITOR}`, {
      state: location.pathname,
    });
  };

  const availableSettingItems = organisationSettingsItems.filter(
    (item) => !item.hidden
  );

  const shouldShowRulesEditor =
    isUserAdmin && !sidebarMenuStore.hasOrganisationAssetLicense();

  return (
    <div className="mt-2">
      {isUserAdmin && (
        <div className="px-4 pb-4">
          <Button
            leadingIcon={<IconUserPlus size={16} stroke={3} className="mr-2" />}
            type="gray"
            size="small"
            className="!h-6 mt-2"
            onClick={showInvite}
          >
            {t("organisationSettings.members.inviteMembers")}
          </Button>
        </div>
      )}
      <div className="border-t border-gray-100">
        {availableSettingItems.map((item, index) => (
          <div
            key={index}
            className={clsx(
              "text-sm font-medium flex py-2 w-full items-center text-gray-700 cursor-pointer",
              "transition hover:bg-gray-100 px-4",
              item.className,
              {
                "opacity-50 !cursor-not-allowed": item.disabled,
              }
            )}
            onClick={() => !item.disabled && item.onClick()}
          >
            {item.icon}
            {item.label}
          </div>
        ))}
      </div>
      {shouldShowRulesEditor && (
        <div>
          <div className="bg-gray-50 text-gray-500 text-xs font-medium py-1 px-4 border-y border-gray-300">
            Circular LCA
          </div>
          <div
            className={clsx(
              "px-4 text-sm font-medium flex py-2 w-full items-center text-gray-700 hover:bg-gray-100 transition cursor-pointer"
            )}
            onClick={openRulesEditor}
          >
            <IconAutomation />
            <span className="ml-2">
              {t("organisationSettings.defineMappingRules")}
            </span>
          </div>
        </div>
      )}
      {openSettings && (
        <OrganisationAccountSettings
          open={openSettings}
          close={() => setOpenSettings(false)}
        />
      )}
      {openMembers && (
        <OrganisationUserManagement
          open={openMembers}
          close={() => setOpenMembers(false)}
        />
      )}
      {openInvite && <InviteMember open={openInvite} close={showInvite} />}
    </div>
  );
}

export default observer(OrganisationSettings);
